import React, { PureComponent } from 'react'
import styled from 'styled-components'
import Avatars from './components/Avatars'
import withLoading from '../../hoc/Loading'
import { IAvatarData } from '../../helpers/data/avatars'
import { ICharacterData } from '../../helpers/data/characterData'
import { getAvatarName } from '../../helpers/data/avatarNames'
import { ReactComponent as StartIcon } from '../../assets/icons/start-button.svg'
import getPalette, { IPalette } from '../../helpers/colors/colors'
import getFont from '../../helpers/fonts/fonts'

import AudioElement from '../../components/audio/AudioElement'
import MainButton from '../../components/buttons/MainButton'

import AssetComponent from '../../components/asset/Asset'
import { IAssetData } from '../../helpers/data/assetData'
import lottieRendererEnums from '../../helpers/enums/lottieRenderer'

const palette: IPalette = getPalette()

const Wrapper = styled.form`
	width: 100%;
	height: 100%;
	background-size: cover;
`

const AvatarsContainer = styled.div`
	width: 100%;
	height: 23%;
	margin: auto;
	position: absolute;
	bottom: 13%;
	left: 0;
	right: 0;
`

const BackgroundContainer = styled.div`
	width: 100%;
	height: 100%;
	margin: auto;
	position: absolute;
	top: 0;
	left: 0;
`

const ScreenReaderOnly = styled.h2`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0,0,0,0);
	white-space: nowrap;
	border: 0;
`

const LoginTitle = styled.div`
	width: 62%;
	height: 16%;
	margin: auto;
	position: absolute;
	right: 0;
	top: 8%;
	left: 0;
`

const LoginTitleText = styled.h2`
	position: absolute;
	top: 38%;
	left: 50%;
	transform: translate(-50%, -50%);
	margin: 0;
	font-family: ${getFont('primaryHeavy')};
	font-size: clamp(1.2rem, 3vw, 2rem);
	color: #005785;
	text-align: center;
	z-index: 10;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
`

const SelectionContainer = styled.div`
	width: 43%;
	height: 57%;
	margin: auto;
	position: absolute;
	top: 13%;
	right: 0;
	left: 0;
`

const Toolbar = styled.div`
	width: 100%;
	height: 8%;
	background-color: ${palette.blackDark};
	display: flex;
	margin: auto;
	position: absolute;
	right: 0;
	bottom: 0;
	left: 0;
	justify-content: flex-end;
`

const SubmitButton = styled.div`
	width: auto;
	height: 100%;
	font-size: 7px;
	padding-right: 24px;
	transition: opacity 0.25s ease-out;
	display: flex;
	align-items: center;
	justify-content: center;
	&.disabled {
		opacity: 0.25;
	}

	@media (min-width: 600px) {
		font-size: 11px;
	}

	@media (min-width: 900px) {
		font-size: 14px;
	}
`

export interface ISkin {
	background: IAssetData
	bottomBar: ISkinBottomBar
	title?: IAssetData
}

interface ISkinBottomBar {
	backgroundColor: string
}

enum StatusTypes {
	IDLE = 'idle',
	SUBMITTING = 'submitting',
}

interface ICharactersProps {
	characterSelected: (id: string) => void
	onViewLoaded: () => void
	characters: ICharacterData[]
	skin: ISkin
	deviceUid: string
}

interface ICharactersState {
	character: string
	nickname: string
	status: StatusTypes
}

class Characters extends PureComponent<ICharactersProps, ICharactersState> {
	private timeoutId?: NodeJS.Timeout

	constructor(props: ICharactersProps) {
		super(props)
		this.state = {
			character: '1',
			nickname: '',
			status: StatusTypes.IDLE,
		}
	}

	onSelect = (id: string) => {
		this.setState({
			character: id,
		})
	}

	handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({
			nickname: ev.target.value,
		})
	}

	handleSubmit = (ev: React.FormEvent) => {
		if (this.state.character && this.state.status === StatusTypes.IDLE) {
			this.props.characterSelected(this.state.character)
			this.setState({
				status: StatusTypes.SUBMITTING,
			})
		}

		ev.stopPropagation()
		ev.preventDefault()
	}

	getCharacterData = (characterId: string) =>
		this.props.characters.find((character) => character.id === characterId) || this.props.characters[0]

	getLoginSelection = (characterId: string) => this.getCharacterData(characterId).loginSelection

	getLoginAudioSelection = (characterId: string) => this.getCharacterData(characterId).loginAudioSelection.path

	componentDidUpdate(prevProps: ICharactersProps, prevState: ICharactersState) {
		if (prevState.status !== this.state.status && this.state.status === StatusTypes.SUBMITTING) {
			// This timeout is a precaution in case the student submission fails for some reason.
			// It will reset the view state after 5 seconds so users can retry again
			this.timeoutId = setTimeout(() => {
				this.setState({
					status: StatusTypes.IDLE,
				})
			}, 5000)
		}
	}

	componentWillUnmount() {
		if (this.timeoutId) {
			clearTimeout(this.timeoutId)
		}
	}

	componentDidMount() {
		// this.updateAnimation()
	}

	onImageLoaded = () => {
		this.props.onViewLoaded()
	}

	render() {
		const isDisabled: boolean = !this.state.character || this.state.status === StatusTypes.SUBMITTING

		const animationData = this.getLoginSelection(this.state.character)

		const avatars = this.props.characters.map(
			(character): IAvatarData => ({
				id: character.id,
				icon: character.loginIcon,
				name: getAvatarName(character.id),
			})
		)

		const loginButtonBackgroundColor = palette.radiantBlue

		return (
			<Wrapper onSubmit={this.handleSubmit}>
				<ScreenReaderOnly>Choose your character</ScreenReaderOnly>
				<BackgroundContainer role="img" aria-label="Choose your character">
					<AssetComponent onLoad={this.onImageLoaded} asset={this.props.skin.background} alt="" />
				</BackgroundContainer>
				{this.props.skin.title && (
					<LoginTitle>
						<AssetComponent asset={this.props.skin.title} aria-hidden="true" />
						<LoginTitleText>Choose your character</LoginTitleText>
					</LoginTitle>
				)}
				<SelectionContainer>
					<AssetComponent
						asset={animationData}
						uid={this.props.deviceUid + this.state.character}
						renderer={lottieRendererEnums.CANVAS}
						alt={`Character ${this.state.character} animation`}
					/>
				</SelectionContainer>
				<AvatarsContainer>
					<Avatars 
					avatars={avatars} 
					onSelect={this.onSelect}
					selectedAvatarId={this.state.character}
				/>
				</AvatarsContainer>
				<Toolbar>
					{/*<NameInput
						placeholder={'Name your character…'}
						onChange={this.handleInputChange}
						value={this.state.nickname}
					/>*/}
					<SubmitButton className={this.state.status === StatusTypes.SUBMITTING ? 'disabled' : ''}>
						<MainButton
							text={'Join game'}
							isDisabled={isDisabled}
							onSubmit={this.handleSubmit}
							isRounded={true}
							backgroundColor={loginButtonBackgroundColor}
							Icon={<StartIcon />}
						/>
					</SubmitButton>
				</Toolbar>
				<AudioElement loop={false} src={this.getLoginAudioSelection(this.state.character)} />
			</Wrapper>
		)
	}
}

export default withLoading(Characters)
